import { useIntl } from 'react-intl';
import SeriesMipPane, {
    NOTE_STRIP_HEIGHT_PX,
} from '~/containers/viewer/viewport/SeriesMipPane';
import type { Image3D } from '~/domain/patient/image3d';
import type { Environment } from '~/engine/data/Environment';
import type { PlaneView } from '~/engine/view/views/PlaneView';
import FittedPane from './FittedPane';

interface FittedSeriesMipPaneProps {
    readonly image: Image3D;
    readonly environment: Environment | undefined;
    /** The view whose plane the line marks; absent, a grid-aligned transversal plane. */
    readonly view: PlaneView | undefined;
    readonly onLocate?: (voxelIndex: number) => void;
}

/**
 * The series overview fitted to its box: the pane is drawn at the box's
 * width, its projection no taller than the box less the note strip, and
 * letterboxed in the middle; nothing is drawn while no room is left under
 * the strip. The hover hint says what the projection shows.
 */
export default function FittedSeriesMipPane({
    image,
    environment,
    view,
    onLocate,
}: FittedSeriesMipPaneProps) {
    const intl = useIntl();
    return (
        <FittedPane
            hint={intl.formatMessage({ id: 'series.mipPreview.tooltip' })}
            testId="fitted-series-mip-pane"
        >
            {(box) => {
                const widthPx = Math.floor(box.widthPx);
                const roomPx = Math.floor(box.heightPx) - NOTE_STRIP_HEIGHT_PX;
                if (widthPx <= 0 || roomPx <= 0) {
                    return null;
                }
                return (
                    <SeriesMipPane
                        image={image}
                        environment={environment}
                        view={view}
                        widthPx={widthPx}
                        maxHeightPx={roomPx}
                        onLocate={onLocate}
                    />
                );
            }}
        </FittedPane>
    );
}
